import { Button, Modal } from '@prio365/prio365-react-library';
import classNames from 'classnames';
import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'react-jss';
import Flex from '../../../../../../components/Flex';
import { PrioTheme } from '../../../../../../theme/types';
import { makePrioStyles } from '../../../../../../theme/utils';
import { useIncomingInvoiceDrawerContext } from './IncomingInvoiceContextProvider';

const useStyles = makePrioStyles((theme) => ({
  root: {
    minWidth: 'fit-content',
  },
  footerContainer: {
    width: '100%',
    minWidth: 'fit-content',
  },
}));

interface IncomingInvoicesUnsavedChangesModalProps {
  className?: string;
  unsavedChanges: boolean;
  onSaveAndContinue: () => void;
  onContinueWithoutSaving: () => void;
}

const IncomingInvoicesUnsavedChangesModal: React.FC<IncomingInvoicesUnsavedChangesModalProps> =
  memo((props) => {
    //#region ------------------------------ Defaults
    const {
      className,
      unsavedChanges,
      onSaveAndContinue,
      onContinueWithoutSaving,
    } = props;

    const classes = useStyles();
    const theme = useTheme<PrioTheme>();
    const { t } = useTranslation();
    //#endregion

    //#region ------------------------------ States / Attributes / Selectors
    const { setUnsavedChangesModalVisible } = useIncomingInvoiceDrawerContext();
    //#endregion

    //#region ------------------------------ Methods / Handlers
    const handleClose = useCallback(() => {
      setUnsavedChangesModalVisible(false);
    }, [setUnsavedChangesModalVisible]);

    const handleContinueWithoutSaving = useCallback(() => {
      onContinueWithoutSaving();
    }, [onContinueWithoutSaving]);

    const handleSaveAndContinue = useCallback(() => {
      onSaveAndContinue();
    }, [onSaveAndContinue]);
    //#endregion

    return (
      <Modal
        className={classNames(classes.root, className)}
        title={t('accounting:incomingInvoices.unsavedChangesModal.title')}
        visible={unsavedChanges}
        onClose={handleClose}
        footer={
          <div className={classes.footerContainer}>
            <Flex.Row justifyContent="space-between" alignItems="center">
              <Button type="default" onClick={handleClose}>
                {t('common:cancel')}
              </Button>

              <Flex.Row childrenGap={theme.spacing.small}>
                <Button type="default" onClick={handleContinueWithoutSaving}>
                  {t(
                    'accounting:incomingInvoices.unsavedChangesModal.continueWithoutSaving'
                  )}
                </Button>
                <Button type="primary" onClick={handleSaveAndContinue}>
                  {t(
                    'accounting:incomingInvoices.unsavedChangesModal.saveAndContinue'
                  )}
                </Button>
              </Flex.Row>
            </Flex.Row>
          </div>
        }
      >
        {t('accounting:incomingInvoices.unsavedChangesModal.description')}
      </Modal>
    );
  });

export default IncomingInvoicesUnsavedChangesModal;
